import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Container,
    Grid,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import axios from "axios";
import AuthContext from "../../context/auth/AuthContext";
import { TrackNowButton, TrackNowSelect, TrackNowTextField } from "../ui";
import { tokens } from "../../theme/tokens";
import { useCommonDetailsContext } from "../../context/Common/common";
import UnAuthorized from "../common/UnAuthorized";
import Loader from "../common/Loader";
import CustomDialog from "../common/CustomDialog";
import { Chip, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const API_BASE_URL = process.env.REACT_APP_TRKNW_API_BASE_URL;

const STEP_TABS = [
    "1. Brand Assets",
    "2. Colors & Fonts",
    "3. Preview",
    "4. Publish",
    "5. Published Version",
];

const FONT_OPTIONS = [
    // Standard & Modern Sans-Serif Fonts
    "Area Normal",
    "Roboto",
    "Inter",
    "Poppins",
    "Open Sans",
    "Lato",
    "Montserrat",
    "Work Sans",
    "Plus Jakarta Sans",
    "DM Sans",
    "Nunito",
    "Raleway",
    "Fira Sans",
    "Rubik",
    "Quicksand",
    "Noto Sans",
    "Josefin Sans",
    "Arial",
    "Verdana",
    "Trebuchet MS",

    // Classic & Standard Serif / Formal Fonts
    "Times New Roman",
    "Georgia",
    "Garamond",
    "Merriweather",
    "Playfair Display",
    "Baskerville",
    "Cambria",
    "Didot",

    // Display & Stylized Fonts
    "Oswald",
    "Newton",
    "Courier New"
];

const LogoUploadField = ({
    fileInputKey,
    logoFile,
    logoPreviewUrl,
    formLogoUrl,
    onFileChange,
    onClear,
    error,
    helperText,
}) => {
    const fileInputRef = useRef(null);

    const getDisplayName = () => {
        if (logoFile?.name) return logoFile.name;
        if (formLogoUrl) {
            const parts = formLogoUrl.split("/").pop().split("?")[0];
            return parts || formLogoUrl;
        }
        if (logoPreviewUrl) {
            if (logoPreviewUrl.startsWith("data:")) return "Selected Image";
            const parts = logoPreviewUrl.split("/").pop().split("?")[0];
            return parts || "Selected Image";
        }
        return "No file chosen";
    };

    const hasFile = Boolean(logoFile || logoPreviewUrl || formLogoUrl);

    return (
        <Stack spacing={0.5}>
            <Typography sx={{ color: tokens.colors.grey, fontSize: "14px" }}>
                Logo Upload
            </Typography>
            <input
                key={fileInputKey}
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    onFileChange(file);
                }}
            />
            <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    height: "48px",
                    minHeight: "48px",
                    borderRadius: "10px",
                    border: `1px solid ${error ? tokens.colors.error : tokens.colors.grey}`,
                    backgroundColor: tokens.colors.offWhite,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "10px",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    "&:hover": {
                        borderColor: error ? tokens.colors.error : tokens.colors.black,
                    },
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: "hidden", pr: 1 }}>
                    <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        sx={{
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: tokens.colors.offBlack,
                            backgroundColor: "#fff",
                            borderColor: tokens.colors.midGrey,
                            borderRadius: "6px",
                            py: "4px",
                            px: "10px",
                            flexShrink: 0,
                            "&:hover": {
                                backgroundColor: "#f5f5f5",
                                borderColor: tokens.colors.grey,
                            },
                        }}
                    >
                        Choose File
                    </Button>
                    <Typography
                        noWrap
                        sx={{
                            fontSize: "14px",
                            color: hasFile ? tokens.colors.offBlack : tokens.colors.grey,
                            fontWeight: hasFile ? 500 : 400,
                        }}
                    >
                        {getDisplayName()}
                    </Typography>
                </Stack>
                {hasFile && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (fileInputRef.current) fileInputRef.current.value = "";
                            onClear();
                        }}
                        sx={{
                            color: tokens.colors.grey,
                            "&:hover": { color: tokens.colors.offBlack },
                        }}
                        title="Clear logo"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helperText && (
                <Typography
                    sx={{
                        fontSize: "12px",
                        color: error ? tokens.colors.error : tokens.colors.grey,
                        mt: "4px",
                        ml: 0,
                    }}
                >
                    {helperText}
                </Typography>
            )}
        </Stack>
    );
};

const ColorField = ({ label, value, onChange, error }) => (
    <Grid item xs={12} md={6}>
        <Stack spacing={1}>
            <Typography sx={{ color: tokens.colors.grey, fontSize: "14px" }}>{label}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <Box
                    component="input"
                    type="color"
                    value={value || "#000000"}
                    onChange={(e) => onChange(e.target.value)}
                    sx={{
                        width: 48,
                        height: 48,
                        border: `1px solid ${tokens.colors.midGrey}`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        backgroundColor: "transparent",
                        p: "4px",
                        flexShrink: 0,
                    }}
                />
                <TrackNowTextField
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    error={Boolean(error)}
                    helperText={error}
                    fullWidth
                    placeholder="#001a77"
                />
            </Stack>
        </Stack>
    </Grid>
);

const loadDynamicFonts = () => {
    const SYSTEM_FONTS = [
        "Times New Roman", "Georgia", "Garamond", "Arial", "Verdana",
        "Trebuchet MS", "Courier New", "Baskerville", "Cambria", "Didot"
    ];
    FONT_OPTIONS.forEach((fontName) => {
        if (!SYSTEM_FONTS.includes(fontName)) {
            const id = `dynamic-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
            if (!document.getElementById(id)) {
                const link = document.createElement("link");
                link.id = id;
                link.rel = "stylesheet";
                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;600;700&display=swap`;
                document.head.appendChild(link);
            }
        }
    });
};

const SearchableFontSelect = ({ value, onChange, error, helperText }) => {
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        loadDynamicFonts();
    }, []);

    const filteredFonts = useMemo(() => {
        if (!searchTerm.trim()) return FONT_OPTIONS;
        return FONT_OPTIONS.filter((font) =>
            font.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    return (
        <TrackNowSelect
            value={value}
            onChange={onChange}
            label="Font Family"
            error={Boolean(error)}
            helperText={helperText}
            fullWidth
            renderValue={(selected) => (
                <span className="tn-font-preview-item" style={{ fontFamily: `'${selected}', sans-serif !important`, '--tn-preview-font': `'${selected}', sans-serif` }}>
                    {selected}
                </span>
            )}
            MenuProps={{
                autoFocus: false,
                PaperProps: {
                    style: { maxHeight: 380, width: 340 }
                }
            }}
        >
            <Box
                sx={{ p: 1, position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2, borderBottom: `1px solid ${tokens.colors.midGrey}` }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <TrackNowTextField
                    placeholder="Search font..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                />
            </Box>

            {filteredFonts.length > 0 ? (
                filteredFonts.map((fontName) => (
                    <MenuItem
                        key={fontName}
                        value={fontName}
                        className="tn-font-preview-item"
                        style={{ '--tn-preview-font': `'${fontName}', sans-serif` }}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.2,
                            px: 2,
                        }}
                    >
                        <Typography
                            className="tn-font-preview-item"
                            style={{ '--tn-preview-font': `'${fontName}', sans-serif` }}
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px"
                            }}
                        >
                            {fontName}
                        </Typography>
                        <Typography
                            className="tn-font-preview-item"
                            style={{ '--tn-preview-font': `'${fontName}', sans-serif` }}
                            sx={{
                                fontSize: "12px",
                                color: tokens.colors.grey
                            }}
                        >
                            Aa Bb 123
                        </Typography>
                    </MenuItem>
                ))
            ) : (
                <MenuItem disabled sx={{ justifyContent: "center", py: 2 }}>
                    No fonts found
                </MenuItem>
            )}
        </TrackNowSelect>
    );
};

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        if (!file) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
    } catch {
        return dateStr;
    }
};

const DEFAULT_BRANDING_FORM = {
    primaryColor: "#001a77",
    backgroundColor: "#001a77",
    fontFamily: "Roboto",
    logoUrl: "",
    logoDataUrl: "",
};

export default function BrandingConfiguration() {
    const authContext = useContext(AuthContext);
    const { authUser, roleType, roleDetails } = authContext;
    const {
        publishedBranding,
        setIsPublishedBranding,
        clientScopedBranding,
        setClientScopedBranding,
        validateParentData,
        fetchValidateParent,
        brandingCacheRef,
    } = useCommonDetailsContext();

    const [activeStep, setActiveStep] = useState(0);
    const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);

    const [fileInputKey, setFileInputKey] = useState(0);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
    const [form, setForm] = useState(DEFAULT_BRANDING_FORM);
    const [published, setPublished] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [errors, setErrors] = useState({});
    const [isPublishing, setIsPublishing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [customerName, setCustomerName] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [isSingleParent, setIsSingleParent] = useState(null);
    const [parentCustomerList, setParentCustomerList] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState("");

    const activeBranding = isSingleParent ? publishedBranding : (clientScopedBranding || publishedBranding);
    const primaryColor = activeBranding?.primaryColor || tokens.colors.connectedPurple;
    const containerBackgroundColor = tokens.colors.offWhite;

    const loggedUser = useMemo(
        () => ({
            userId: authUser?.userId ?? "-",
            userName: authUser?.userName || authUser?.loginId || "Unknown User",
        }),
        [authUser]
    );

    const isBrandingRoleIncludes = Array.isArray(roleType)
        ? roleType.some((role) =>
            [
                "Admin",
                "Admin-PostAward",
                "ClientAdmin",
                "ClientAdmin-PostAward",
                "HierarchyAdmin",
                "HierarchyAdmin-PostAward",
            ].includes(role)
        )
        : [
            "Admin",
            "Admin-PostAward",
            "ClientAdmin",
            "ClientAdmin-PostAward",
            "HierarchyAdmin",
            "HierarchyAdmin-PostAward",
        ].includes(roleType);

    const fetchBranding = async (customerId, isSingle = isSingleParent) => {
        if (!customerId) return null;
        try {
            const res = await axios.get(
                `${API_BASE_URL}/Application/GetBrandingConfiguration?CustomerId=${customerId}&CustomerType=Parent`
            );

            const data = res.data?.data || (Array.isArray(res.data) ? res.data[0] : res.data);
            console.log("Fetched branding configuration:", res, res.data, data);
            if (data && (data.primaryColor || data.PrimaryColor || data.customerId || data.CustomerId || data.brandingId || data.BrandingId)) {
                const rawBgColor = data.backgroundColor || data.BackgroundColor;
                const normalized = {
                    brandingId: data.brandingId ?? data.BrandingId ?? data.id ?? data.Id,
                    customerId: data.customerId ?? data.CustomerId ?? customerId,
                    primaryColor: data.primaryColor || data.PrimaryColor || "#001a77",
                    // Treat stored "#ffffff" as unset — it was the old bad default (tokens.colors.offWhite = "#ffffff")
                    backgroundColor: (!rawBgColor || rawBgColor === "#ffffff") ? "#001a77" : rawBgColor,
                    fontFamily: data.fontFamily || data.FontFamily || "Roboto",
                    logoDataUrl: data.logoDataUrl || data.LogoDataUrl || "",
                    userId: data.userId ?? data.UserId ?? "",
                    userName: data.userName || data.UserName || data.createUser || data.CreateUser || "",
                    createUser: data.createUser || data.CreateUser || "",
                    createDate: data.createDate || data.CreateDate || data.publishedAt || data.PublishedAt || "",
                    updateUser: data.updateUser || data.UpdateUser || "",
                    updateDate: data.updateDate || data.UpdateDate || "",
                    version: data.version ?? data.Version,
                };
                setPublished(normalized);
                setForm({
                    primaryColor: normalized.primaryColor,
                    backgroundColor: normalized.backgroundColor,
                    fontFamily: normalized.fontFamily,
                    logoUrl: "",
                    logoDataUrl: normalized.logoDataUrl,
                });
                if (normalized.logoDataUrl) {
                    setLogoPreviewUrl(normalized.logoDataUrl);
                } else {
                    setLogoPreviewUrl("");
                }
                setLogoFile(null);
                setFileInputKey((prev) => prev + 1);

                if (isSingle) {
                    setIsPublishedBranding(normalized);
                } else {
                    setClientScopedBranding(normalized);
                }
                return normalized;
            } else {
                setPublished(null);
                setForm(DEFAULT_BRANDING_FORM);
                setLogoPreviewUrl("");
                setLogoFile(null);
                setFileInputKey((prev) => prev + 1);
                if (!isSingle) {
                    setClientScopedBranding(null);
                }
                return null;
            }
        } catch (error) {
            console.error("Error fetching branding configuration:", error);
            setPublished(null);
            setForm(DEFAULT_BRANDING_FORM);
            setLogoPreviewUrl("");
            setLogoFile(null);
            setFileInputKey((prev) => prev + 1);
            if (!isSingle) {
                setClientScopedBranding(null);
            }
            return null;
        }
    };

    const fetchHistory = async (customerId) => {
        if (!customerId) return;
        try {
            const res = await axios.get(
                `${API_BASE_URL}/Application/GetBrandingConfigurationHistory?CustomerId=${customerId}`
            );

            let list = [];
            const data = res.data;

            // Extract the list from various response structures
            if (Array.isArray(data)) {
                list = data;
            } else if (data && typeof data === "object") {
                if (Array.isArray(data.result || data.Result)) {
                    list = data.result || data.Result;
                } else if (Array.isArray(data.history || data.History)) {
                    list = data.history || data.History;
                } else if (Array.isArray(data.data || data.Data)) {
                    list = data.data || data.Data;
                } else {
                    list = [data];
                }
            }

            console.log("Fetch history list", list);

            // Store original list directly into state
            setHistoryRows(list);
        } catch (error) {
            console.error("Error fetching branding history:", error);
            setHistoryRows([]);
        }
    };
    const fetchParentCustomerList = async () => {
        try {
            const hasAdminRole = Array.isArray(roleType)
                ? roleType.some((role) =>
                    ["Admin", "Admin-PostAward"].includes(role)
                )
                : ["Admin", "Admin-PostAward"].includes(roleType);

            let url = `${API_BASE_URL}/Customer/ParentCustomersList`;
            const currentUserId = authUser?.userId ?? authUser?.authId;
            if (isBrandingRoleIncludes && !hasAdminRole && currentUserId !== undefined && currentUserId !== null) {
                url += `?userId=${currentUserId}`;
            }

            const resParentList = await axios.get(url);
            let list = Array.isArray(resParentList.data?.data)
                ? resParentList.data.data
                : Array.isArray(resParentList.data)
                    ? resParentList.data
                    : [];
            let filt
            if (hasAdminRole) {
                filt = list
            } else {
                filt = list.filter(item => item.roleName.includes("Admin",
                    "Admin-PostAward",
                    "ClientAdmin",
                    "ClientAdmin-PostAward",
                    "HierarchyAdmin",
                    "HierarchyAdmin-PostAward"))
            }

            let _data = filt.map((ele) => ({
                code: ele.customerId ?? ele.code ?? ele.parentCustomerId ?? ele.id,
                name: ele.customerName ?? ele.name ?? ele.parentCustomerName,
            }));
            _data = _data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setParentCustomerList(_data);
        } catch (error) {
            console.error("fetchParentCustomerList error", error);
            setParentCustomerList([]);
        }
    };

    const handleClientChange = async (e) => {
        const custId = e.target.value;
        setSelectedCustomerId(custId);
        setErrors((prev) => ({ ...prev, selectedCustomerId: undefined }));
        if (custId) {
            setIsLoading(true);
            const brandingResult = await fetchBranding(custId, false);
            await fetchHistory(custId);
            // Populate form from fetched branding if available, else use defaults
            if (brandingResult) {
                setForm({
                    primaryColor: brandingResult.primaryColor || "#001a77",
                    backgroundColor: brandingResult.backgroundColor || "#001a77",
                    fontFamily: brandingResult.fontFamily || "Roboto",
                    logoUrl: brandingResult.logoDataUrl || "",
                });
                if (brandingResult.logoDataUrl) {
                    setLogoPreviewUrl(brandingResult.logoDataUrl);
                } else {
                    setLogoPreviewUrl("");
                }
            } else {
                setForm(DEFAULT_BRANDING_FORM);
                setLogoPreviewUrl("");
            }
            setLogoFile(null);
            setFileInputKey((prev) => prev + 1);
            setMaxUnlockedStep(0);
            setActiveStep(0);
            setIsLoading(false);
        } else {
            setPublished(null);
            setClientScopedBranding(null);
            setHistoryRows([]);
            setForm(DEFAULT_BRANDING_FORM);
            setLogoFile(null);
            setLogoPreviewUrl("");
            setFileInputKey((prev) => prev + 1);
            setMaxUnlockedStep(0);
            setActiveStep(0);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            const currentUserId = authUser?.userId ?? authUser?.authId;
            // Explicitly check for null/undefined so 0 is accepted as a valid userId for Admin
            if (currentUserId === undefined || currentUserId === null) return;
            setIsLoading(true);

            let checkCust = validateParentData;
            if (!checkCust && fetchValidateParent) {
                checkCust = await fetchValidateParent(currentUserId);
            }

            console.log("BrandingConfiguration validateParentData:", checkCust);

            // Dynamically show/hide dropdown based on isSingleParent returned by API:
            // isSingleParent: true  => hide dropdown and load that customer's branding directly
            // isSingleParent: false => show dropdown and load client list
            if (checkCust?.isSingleParent) {
                setIsSingleParent(true);
                setCustomerId(checkCust?.parentCustomerId);
                const targetCustId = checkCust?.parentCustomerId;
                console.log("targetCustId", targetCustId);

                await fetchBranding(targetCustId, true);
                await fetchHistory(targetCustId);
            } else {
                setIsSingleParent(false);
                setCustomerId("");
                setClientScopedBranding(null);
                // isSingleParent === false: fetch parent customer list for dropdown, don't load branding yet
                await fetchParentCustomerList();
            }

            setIsLoading(false);
        };
        loadInitialData();
    }, [authUser?.userId, authUser?.authId, validateParentData, roleType]);

    useEffect(() => {
        return () => {
            if (setClientScopedBranding) {
                setClientScopedBranding(null);
            }
        };
    }, [setClientScopedBranding]);

    useEffect(() => {
        loadDynamicFonts();
    }, []);

    useEffect(() => {
        let isMounted = true;
        if (logoFile) {
            fileToDataUrl(logoFile).then((dataUrl) => {
                if (isMounted) {
                    const resolved = dataUrl || "";
                    setForm((prev) => ({ ...prev, logoDataUrl: resolved }));
                    setLogoPreviewUrl(resolved);
                }
            });
            return () => {
                isMounted = false;
            };
        }
        if (form.logoUrl) {
            setLogoPreviewUrl(form.logoUrl);
            return;
        }
        if (form.logoDataUrl) {
            setLogoPreviewUrl(form.logoDataUrl);
            return;
        }
        setLogoPreviewUrl("");
    }, [logoFile, form.logoUrl]);
    useEffect(() => {
        const colorChck = tokens.colors.connectedPurple
        console.log("form", form, publishedBranding, colorChck);

        console.log("historyRows", historyRows);


    }, [form, publishedBranding, historyRows])
    const validateStep = (step) => {
        const nextErrors = {};

        if (step === 0 && isSingleParent === false) {
            if (!selectedCustomerId) {
                nextErrors.selectedCustomerId = "Please select a client to proceed";
            }
        }

        if (step === 1) {
            if (!form.primaryColor) nextErrors.primaryColor = "Primary color is required";
            if (!form.backgroundColor) nextErrors.backgroundColor = "Background color is required";
            if (!form.fontFamily) nextErrors.fontFamily = "Font is required";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const goNext = () => {
        if (!validateStep(activeStep)) return;

        const nextStep = Math.min(activeStep + 1, 3);
        setMaxUnlockedStep((prev) => Math.max(prev, nextStep));
        setActiveStep(nextStep);
    };

    const goBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const onTabClick = (_event, stepIndex) => {
        if (stepIndex === 0 || stepIndex <= maxUnlockedStep || (stepIndex === 4 && published)) {
            setActiveStep(stepIndex);
        }
    };

    const isFormChanged = useMemo(() => {
        if (!published) {
            return true;
        }

        const normalizeColor = (c) => (c || "").trim().toLowerCase();
        const normalizeText = (t) => (t || "").trim();

        const primaryChanged = normalizeColor(form.primaryColor) !== normalizeColor(published.primaryColor);
        const backgroundChanged = normalizeColor(form.backgroundColor) !== normalizeColor(published.backgroundColor);
        const fontChanged = normalizeText(form.fontFamily) !== normalizeText(published.fontFamily);

        const currentLogo = normalizeText(form.logoUrl || form.logoDataUrl || logoPreviewUrl);
        const publishedLogo = normalizeText(published.logoUrl || published.logoDataUrl);
        const logoChanged = currentLogo !== publishedLogo;

        return primaryChanged || backgroundChanged || fontChanged || logoChanged;
    }, [form, published, logoPreviewUrl]);

    const publishNow = async () => {
        if (!isFormChanged) return;

        if (!validateStep(0)) {
            setActiveStep(0);
            return;
        }

        if (!validateStep(1)) {
            setActiveStep(1);
            return;
        }

        const targetCustId = isSingleParent ? customerId : selectedCustomerId;
        if (!targetCustId) {
            setDialogTitle("Info");
            setDialogMessage(isSingleParent ? "Customer ID is required to save branding configuration." : "Please select a client before publishing.");
            setOpenDialog(true);
            return;
        }

        setIsPublishing(true);

        try {
            let hasExistingBranding = false;
            try {
                const checkRes = await axios.get(
                    `${API_BASE_URL}/Application/GetBrandingConfiguration?CustomerId=${targetCustId}&CustomerType=Parent`
                );
                const resData = checkRes.data;
                const data = resData?.data || (Array.isArray(resData) ? resData[0] : resData);
                const isNotFound =
                    resData?.status === "Failure" ||
                    resData?.errorMessage === "Branding Configuration Not Found" ||
                    !data ||
                    (!data.primaryColor && !data.PrimaryColor && !data.brandingId && !data.BrandingId);

                if (!isNotFound && (data.primaryColor || data.PrimaryColor || data.brandingId || data.BrandingId)) {
                    hasExistingBranding = true;
                }
            } catch (err) {
                console.log("GetBrandingConfiguration check error in BrandingConfiguration:", err);
                hasExistingBranding = false;
            }

            if (!hasExistingBranding) {
                const defaultPayload = {
                    customerId: targetCustId,
                    primaryColor: DEFAULT_BRANDING_FORM.primaryColor || "#001a77",
                    backgroundColor: DEFAULT_BRANDING_FORM.backgroundColor || "#001a77",
                    fontFamily: DEFAULT_BRANDING_FORM.fontFamily || "Roboto",
                    isDefault: true,
                    logoDataUrl: "",
                    userId: String(authUser?.userId ?? ""),
                    userName: authUser?.userName || authUser?.loginId || "",
                    createUser: String(authUser?.userId ?? ""),
                };
                console.log("Creating default branding configuration first:", defaultPayload);
                await axios.post(
                    `${API_BASE_URL}/Application/CreateBrandingConfiguration`,
                    defaultPayload
                );
            }

            const current = { history: historyRows, published: published || publishedBranding };
            const maxVersion = current.history.reduce(
                (max, row) => (row.version > max ? row.version : max),
                current.published?.version || 0
            );

            const logoDataUrl = logoFile
                ? (form.logoDataUrl || await fileToDataUrl(logoFile))
                : (form.logoUrl || form.logoDataUrl || "");

            const now = new Date().toISOString();
            const version = maxVersion + 1;

            const payload = {
                customerId: targetCustId,
                primaryColor: form.primaryColor,
                backgroundColor: form.backgroundColor,
                fontFamily: form.fontFamily,
                isDefault: true,
                logoDataUrl: logoDataUrl || "",
                userId: String(authUser?.userId ?? ""),
                userName: authUser?.userName || authUser?.loginId || "",
                createUser: String(authUser?.userId ?? ""),
            };

            console.log("CreateBrandingConfiguration payload:", payload, form);
            await axios.post(
                `${API_BASE_URL}/Application/CreateBrandingConfiguration`,
                payload
            );

            if (targetCustId) {
                const key1 = String(targetCustId);
                const key2 = `${targetCustId}_Parent`;
                if (brandingCacheRef?.current?.has(key1)) {
                    brandingCacheRef.current.delete(key1);
                }
                if (brandingCacheRef?.current?.has(key2)) {
                    brandingCacheRef.current.delete(key2);
                }
            }

            setDialogTitle("Success");
            setDialogMessage("Branding configuration published successfully!");
            setOpenDialog(true);

            const updatedBranding = await fetchBranding(targetCustId, isSingleParent);
            await fetchHistory(targetCustId);

            if (updatedBranding) {
                if (isSingleParent) {
                    setIsPublishedBranding(updatedBranding);
                } else {
                    setClientScopedBranding(updatedBranding);
                }
            }

            setErrors({});
            setMaxUnlockedStep(0);
            setActiveStep(4);
        } catch (error) {
            console.error("Error publishing branding configuration:", error);
            setDialogTitle("Error");
            setDialogMessage("Failed to publish branding configuration. Please try again.");
            setOpenDialog(true);
        } finally {
            setIsPublishing(false);
        }
    };
    const handleApplyTheme = async (row) => {
        console.log("handleApplyTheme", "row", row);
        try {
            setIsLoading(true);
            const payload = {
                customerId: row.customerId,
                brandingId: row.brandingId,
                isDefault: true,
                userId: row.userId,
                userName: row.userName
            }
            const res = await axios.put(
                `${API_BASE_URL}/Application/UpdateBrandingDefaultStatus`, payload
            );
            console.log("handleApplyTheme res", res);
            if (row?.customerId) {
                const key1 = String(row.customerId);
                const key2 = `${row.customerId}_Parent`;
                if (brandingCacheRef?.current?.has(key1)) {
                    brandingCacheRef.current.delete(key1);
                }
                if (brandingCacheRef?.current?.has(key2)) {
                    brandingCacheRef.current.delete(key2);
                }
            }
            const refetchCustId = isSingleParent ? customerId : (selectedCustomerId || row.customerId);
            const updatedBranding = await fetchBranding(refetchCustId, isSingleParent);
            await fetchHistory(refetchCustId);

            if (updatedBranding) {
                if (isSingleParent) {
                    setIsPublishedBranding(updatedBranding);
                } else {
                    setClientScopedBranding(updatedBranding);
                }
            }
            setDialogTitle("Success");
            setDialogMessage("Theme applied successfully!");
            setOpenDialog(true);
        } catch (error) {
            console.error("Error applying theme:", error);
            setDialogTitle("Error");
            setDialogMessage("Failed to apply theme. Please try again.");
            setOpenDialog(true);
        } finally {
            setIsLoading(false);
        }
    }
    const renderContent = () => {
        if (activeStep === 0) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Brand Assets
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <LogoUploadField
                                fileInputKey={fileInputKey}
                                logoFile={logoFile}
                                logoPreviewUrl={logoPreviewUrl}
                                formLogoUrl={form.logoUrl}
                                onFileChange={(file) => setLogoFile(file)}
                                onClear={() => {
                                    setLogoFile(null);
                                    setForm((prev) => ({ ...prev, logoUrl: "", logoDataUrl: "" }));
                                    setLogoPreviewUrl("");
                                    setFileInputKey((prev) => prev + 1);
                                }}
                                error={Boolean(errors.logo)}
                                helperText={errors.logo || "Accepted: png, jpg, svg"}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TrackNowTextField
                                label="Logo URL (optional)"
                                value={form.logoUrl}
                                onChange={(e) => setForm((prev) => ({ ...prev, logoUrl: e.target.value }))}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Box>
                        <Typography sx={{ color: tokens.colors.grey, fontSize: "14px", mb: 1 }}>Logo Preview</Typography>
                        <Paper sx={{ p: 2, minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {logoPreviewUrl ? <img src={logoPreviewUrl} alt="logo preview" style={{ maxHeight: 72, maxWidth: "100%" }} /> : <Typography color="text.secondary">No logo selected</Typography>}
                        </Paper>
                    </Box>

                    <Stack direction="row" justifyContent="flex-end">
                        <TrackNowButton variant="primary" onClick={goNext}>Save & Continue</TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        if (activeStep === 1) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Colors & Fonts
                    </Typography>

                    <Grid container spacing={2}>
                        <ColorField
                            label="Primary Color"
                            value={form.primaryColor}
                            onChange={(val) => setForm((prev) => ({ ...prev, primaryColor: val }))}
                            error={errors.primaryColor}
                        />
                        <ColorField
                            label="Background Color"
                            value={form.backgroundColor}
                            onChange={(val) => setForm((prev) => ({ ...prev, backgroundColor: val }))}
                            error={errors.backgroundColor}
                        />
                        <Grid item xs={12} md={6}>
                            <SearchableFontSelect
                                value={form.fontFamily}
                                onChange={(e) => setForm((prev) => ({ ...prev, fontFamily: e.target.value }))}
                                error={Boolean(errors.fontFamily)}
                                helperText={errors.fontFamily}
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="space-between">
                        <TrackNowButton variant="tertiary" onClick={goBack}>Back</TrackNowButton>
                        <TrackNowButton variant="primary" onClick={goNext}>Save & Continue</TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        if (activeStep === 2) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Preview
                    </Typography>

                    <Paper
                        className="tn-font-preview-item"
                        style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: `1px solid ${tokens.colors.midGrey}`,
                            backgroundColor: form.backgroundColor,
                            fontFamily: `'${form.fontFamily}', sans-serif`,
                        }}
                    >
                        <Container
                            disableGutters
                            className="tn-font-preview-item"
                            style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                            sx={{
                                backgroundColor: tokens.colors.white,
                                borderRadius: 2,
                                px: { xs: 2, sm: 3 },
                                py: 3,
                                fontFamily: `'${form.fontFamily}', sans-serif`,
                            }}
                        >
                            <Box
                                className="tn-font-preview-item"
                                style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: form.primaryColor,
                                    color: "#fff",
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {logoPreviewUrl ? (
                                        <img src={logoPreviewUrl} alt="brand logo" style={{ maxHeight: 38 }} />
                                    ) : (
                                        <Box sx={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.3)" }} />
                                    )}
                                    <Typography
                                        className="tn-font-preview-item"
                                        style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                                        sx={{ fontWeight: 700, fontFamily: `'${form.fontFamily}', sans-serif` }}
                                    >
                                        TrackNow Branding Preview
                                    </Typography>
                                </Stack>
                                <Typography
                                    className="tn-font-preview-item"
                                    style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                                    sx={{ fontSize: "13px", fontFamily: `'${form.fontFamily}', sans-serif` }}
                                >
                                    {loggedUser.userName}
                                </Typography>
                            </Box>

                            <Stack spacing={2} sx={{ mt: 3 }}>
                                <Typography
                                    className="tn-font-preview-item"
                                    style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                                    sx={{ color: "#333", fontSize: "15px", fontFamily: `'${form.fontFamily}', sans-serif` }}
                                >
                                    This is sample text to preview the selected branding, font family, and logo before publish.
                                </Typography>
                                <Box sx={{ display: "inline-flex" }}>
                                    <Box
                                        className="tn-font-preview-item"
                                        style={{ '--tn-preview-font': `'${form.fontFamily}', sans-serif` }}
                                        sx={{
                                            px: 3,
                                            py: 1,
                                            borderRadius: "999px",
                                            color: "#fff",
                                            backgroundColor: form.primaryColor,
                                            fontWeight: 700,
                                            fontFamily: `'${form.fontFamily}', sans-serif`,
                                        }}
                                    >
                                        Primary Button
                                    </Box>
                                </Box>
                            </Stack>
                        </Container>
                    </Paper>

                    <Stack direction="row" justifyContent="space-between">
                        <TrackNowButton variant="tertiary" onClick={goBack}>Back</TrackNowButton>
                        <TrackNowButton variant="primary" onClick={goNext}>Continue</TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        if (activeStep === 3) {
            return (
                <Stack spacing={3}>
                    <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                        Publish
                    </Typography>

                    <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${tokens.colors.midGrey}` }}>
                        <Stack spacing={1.5}>
                            <Typography sx={{ fontWeight: 700, color: primaryColor }}>Summary</Typography>
                            <Typography><strong>Primary:</strong> {form.primaryColor}</Typography>
                            <Typography><strong>Background:</strong> {form.backgroundColor}</Typography>
                            <Typography><strong>Font:</strong> {form.fontFamily}</Typography>
                            <Box>
                                <Typography sx={{ mb: 1 }}><strong>Logo:</strong></Typography>
                                <Paper sx={{ p: 2, minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {logoPreviewUrl ? <img src={logoPreviewUrl} alt="publish logo preview" style={{ maxHeight: 72, maxWidth: "100%" }} /> : <Typography color="text.secondary">No logo selected</Typography>}
                                </Paper>
                            </Box>
                        </Stack>
                    </Paper>

                    <Stack direction="row" justifyContent="space-between">
                        <TrackNowButton variant="tertiary" onClick={goBack} disabled={isPublishing}>Back</TrackNowButton>
                        <TrackNowButton variant="primary" onClick={publishNow} disabled={isPublishing || !isFormChanged}>
                            {isPublishing ? "Publishing..." : "Publish Now"}
                        </TrackNowButton>
                    </Stack>
                </Stack>
            );
        }

        return (
            <Stack spacing={3}>
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                    Published Version
                </Typography>

                {published ? (
                    <>
                        {/* Informational Banner about V1 Default Theme */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: "rgba(0, 26, 119, 0.04)",
                                border: "1px solid rgba(0, 26, 119, 0.15)",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <InfoOutlinedIcon sx={{ color: primaryColor, fontSize: 24, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "14px", color: tokens.colors.offBlack }}>
                                <strong>Theme Information:</strong> <strong>Version 1 (V1)</strong> is the <strong>Default Theme</strong>. You can switch back to V1 (Default Theme) or any previous version at any time from the version history below.
                            </Typography>
                        </Box>

                        <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${tokens.colors.midGrey}` }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <Typography><strong>Version:</strong> {published.version ? `v${published.version}` : (published.brandingId ? `#${published.brandingId}` : "-")}</Typography>
                                    <Typography><strong>Published At:</strong> {formatDate(published.createDate || published.updateDate || published.publishedAt)}</Typography>
                                    <Typography><strong>Published By:</strong> {published.userName || published.createUser || "-"}</Typography>
                                    <Typography><strong>Font:</strong> {published.fontFamily}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    {published.logoDataUrl ? <img src={published.logoDataUrl} alt="published logo" style={{ maxHeight: 72, maxWidth: "100%" }} /> : null}
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                <Box sx={{ width: 24, height: 24, borderRadius: "4px", backgroundColor: published.primaryColor }} />
                                <Box sx={{ width: 24, height: 24, borderRadius: "4px", backgroundColor: published.backgroundColor || tokens.colors.white, border: `1px solid ${tokens.colors.midGrey}` }} />
                            </Stack>
                        </Paper>

                        <Divider />

                        <Typography sx={{ fontWeight: 700, color: primaryColor }}>Version History</Typography>
                        <Table size="small" sx={{ backgroundColor: "#fff", borderRadius: 2, overflow: "hidden" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Published By</TableCell>
                                    <TableCell>Published At</TableCell>
                                    <TableCell>Font</TableCell>
                                    <TableCell>Primary</TableCell>
                                    <TableCell>Background</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyRows.length > 0 ? (
                                    historyRows.map((row, idx) => {
                                        const isCurrent = Boolean(row.isDefault ?? row.IsDefault);

                                        return (
                                            <TableRow key={row.brandingId || `${row.version}-${row.createDate}-${idx}`}>
                                                <TableCell>{row.version ? `v${row.version}` : `#${row.brandingId || idx + 1}`}</TableCell>
                                                <TableCell>{row.userName || row.createUser || "-"}</TableCell>
                                                <TableCell>{formatDate(row.createDate || row.updateDate || row.publishedAt)}</TableCell>
                                                <TableCell>{row.fontFamily}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: "inline-block", width: 16, height: 16, borderRadius: "3px", backgroundColor: row.primaryColor }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "inline-block",
                                                            width: 16,
                                                            height: 16,
                                                            borderRadius: "3px",
                                                            backgroundColor: row.backgroundColor || tokens.colors.white,
                                                            border: `1px solid ${tokens.colors.midGrey}`
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {isCurrent ? (
                                                        <Chip
                                                            label="Active Theme"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                                        />
                                                    ) : (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleApplyTheme(row)}
                                                            sx={{ textTransform: "none", py: 0.25, px: 1.5 }}
                                                        >
                                                            Apply
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No history records available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </>
                ) : (
                    <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${tokens.colors.midGrey}` }}>
                        <Typography color="text.secondary">No published version found yet.</Typography>
                    </Paper>
                )}
            </Stack>
        );
    };

    if (!isBrandingRoleIncludes) {
        console.log("authUser BN", authUser);

        return <UnAuthorized />;
    }

    return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", px: { xs: 1, sm: 2, md: 3, lg: 4 } }}>
            {isLoading && <Loader />}
            <Box sx={{ width: "100%", maxWidth: "1700px" }}>
                <Paper sx={{ p: 2.5, borderRadius: 3, backgroundColor: containerBackgroundColor }}>

                    {isSingleParent === false && (
                        <Box
                            sx={{
                                p: 2,
                                mb: 2.5,
                                borderRadius: 2,
                                backgroundColor: "rgba(0, 26, 119, 0.04)",
                                border: "1px solid rgba(0, 26, 119, 0.15)",
                            }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={6} lg={4}>
                                    <TrackNowSelect
                                        label="Select Client"
                                        value={selectedCustomerId}
                                        onChange={handleClientChange}
                                        error={Boolean(errors.selectedCustomerId)}
                                        helperText={errors.selectedCustomerId}
                                        fullWidth
                                        required
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: tokens.colors.grey }}>Select a client...</span>;
                                            }
                                            const found = parentCustomerList.find((c) => c.code === selected);
                                            return found ? found.name : selected;
                                        }}
                                    >
                                        {parentCustomerList.map((client) => (
                                            <MenuItem key={client.code} value={client.code}>
                                                {client.name}
                                            </MenuItem>
                                        ))}
                                    </TrackNowSelect>
                                </Grid>
                            </Grid>
                            {selectedCustomerId && (
                                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ fontSize: "13px", color: tokens.colors.grey }}>
                                        Configuring theme for: <strong style={{ color: tokens.colors.offBlack }}>{parentCustomerList.find((c) => c.code === selectedCustomerId)?.name}</strong>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4} lg={3}>
                            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
                                <Tabs
                                    value={activeStep}
                                    onChange={onTabClick}
                                    orientation="vertical"
                                    variant="scrollable"
                                    sx={{
                                        minHeight: 420,
                                        "& .MuiTab-root": {
                                            alignItems: "flex-start",
                                            justifyContent: "flex-start",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "14px",
                                            color: tokens.colors.grey,
                                            px: 2,
                                            py: 1.6,
                                        },
                                        "& .Mui-selected": {
                                            color: `${primaryColor} !important`,
                                            backgroundColor: `color-mix(in srgb, ${primaryColor} 10%, transparent)`,
                                        },
                                        "& .MuiTabs-indicator": {
                                            backgroundColor: primaryColor,
                                            width: "4px",
                                            left: 0,
                                        },
                                    }}
                                >
                                    {STEP_TABS.map((stepTitle, idx) => (
                                        <Tab
                                            key={stepTitle}
                                            label={stepTitle}
                                            disabled={!(idx === 0 || idx <= maxUnlockedStep || (idx === 4 && Boolean(published)))}
                                        />
                                    ))}
                                </Tabs>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={8} lg={9}>
                            <Paper sx={{ p: 3, borderRadius: 2, minHeight: 520 }}>{renderContent()}</Paper>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
            <CustomDialog
                header={dialogTitle}
                visible={openDialog}
                onHide={() => setOpenDialog(false)}
            >
                {dialogMessage}
            </CustomDialog>
        </Box>
    );
}
